<template>
	<div
		class="contact-actions-container"
		aria-label="container for search, filter, add and import contacts"
	>
		<DebouncedSearchInput
			class="contact-search"
			ariaLabel="`Search for contacts`"
			placeholder="Search contacts..."
			:value="searchTerm"
			:onInit="onInit"
			:cancelSearch="cancelSearch"
			@onCancelSearch="handleCancelSearch"
			@onSearchInputChange="handleSearchInputChange"
		/>
		<OwnerFilter
			v-if="canPerformProfileSelector"
			class="owner-menu"
			caret
			:brokerList="sortedBrokerShopUsers"
			:selectedBrokers="selectedContactsIds"
			:onInit="onInit"
			:cancelBrokerChange="cancelBrokerChange"
			@onCancelBrokerChange="handleCancelBrokerChange"
			@onBrokerFilterChange="handleBrokerChange"
		/>
		<MultipleFilters
			class="multiple-filters"
			:filterOptions="allContactFilters"
			:loading="loading"
			:selectedFilters="selectedFilters"
			:onInit="onInit"
			:cancelFilterChange="cancelFilterChange"
			@onCancelFilterChange="handleCancelFilterChange"
			@onFilterChange="handleFilterChange"
		/>
		<QuickActionsMenu
			class="add-contacts"
			data-tour="cc-add-contacts-button"
			:buttonText="quickActionButtonText"
			addContacts
			darkButton
			:disabled="!activeContacts"
			:menus="getContactsQuickActions()"
			@addSingleContact="handleAddSingleContact"
			@showContactsImportModal="setShowMultipleContactsImportModal(true)"
			@exportContacts="handleExportContacts"
		/>
	</div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { disableAllQuickActions } from '../helpers';
import { TYPES } from '../constants';
import { VIEWS as MODAL_VIEWS } from '../modals/constants';

import DebouncedSearchInput from '@/components/global/DebouncedSearchInput.vue';
import OwnerFilter from '@/components/brand-builder/OwnerFilter.vue';
import MultipleFilters from '@/components/global/MultipleFilters.vue';
import QuickActionsMenu from '../QuickActionsMenu.vue';

export default {
	name: 'ContactFilters',
	components: {
		DebouncedSearchInput,
		OwnerFilter,
		MultipleFilters,
		QuickActionsMenu,
	},
	props: {
		activeContacts: Boolean,
		filterStatus: {
			type: String,
			required: true,
		},
		selectAllMode: {
			type: Boolean,
			required: true,
		},
		selectedLeadsCount: {
			type: Number,
			required: true,
		},
	},
	data() {
		return {
			currentFilter: '',
			newBrokers: [],
			newContactTypeFilters: [],
			newLoanPurposeFilters: [],
			newLoanTypeFilters: [],
			newSearch: '',
			cancelSearch: false,
			cancelBrokerChange: false,
			cancelFilterChange: false,
			loading: false,
			selectedFilters: {},
		};
	},
	computed: {
		...mapGetters([
			'allContactFilters',
			'sortedBrokerShopUsers',
			'isPermissionActive',
		]),
		...mapState({
			initialTotalCount: (state) => state.ccContacts.leads.initialTotalCount,
			searchTerm: (state) => state.ccContacts.filters.searchTerm,
			onInit: (state) => state.ccContacts.leads.onInit,
			canPerformProfileSelector: (state) =>
				state.profileSwitcher.mainProfileSelector.canPerformProfileSelector,
			selectedContactsIds: (state) => state.clientConnectV2.selectedContactsIds,
			typesIds: (state) => state.ccContacts.filters.typesIds,
			loanTypeIds: (state) => state.ccContacts.filters.loanTypeIds,
			loanPurposeIds: (state) => state.ccContacts.filters.loanPurposeIds,
			userRole: (state) => state.profile.roles,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
		}),
		quickActionButtonText() {
			return this.userRole.includes('BrokerOwner')
				? 'Manage Contacts'
				: 'Add Contacts';
		},
	},
	methods: {
		...mapActions([
			'handleAddSingleContact',
			'exportContacts',
			'getAllLeadTypes',
			'getLoanTypes',
			'getLoanPurposeTypes',
			'setLeadsOninit',
			'setModalView',
			'setSelectedLeadIds',
			'setShowMultipleContactsImportModal',
			'setShowOverviewModal',
			'updatefiltersSearchTerm',
			'updatefiltersLoanPurposeIds',
			'updatefiltersLoanTypeIds',
			'updatefiltersTypesIds',
			'updateSelectedContactsIds',
		]),
		getContactsQuickActions() {
			const addSingleContact = {
				icon: ['far', 'user-alt'],
				text: 'Add Single Contact',
				action: 'addSingleContact',
				disabled: false,
			};

			const showContactsImportModal = {
				icon: ['far', 'cloud-upload'],
				text: 'Import Multiple Contacts',
				action: 'showContactsImportModal',
				disabled: false,
			};
			const roles = ['LoanOfficer', 'MarketingManager'];
			roles.forEach((role) => {
				if (this.userRole.includes(role)) {
					showContactsImportModal.disabled = !this.isPermissionActive(
						role,
						'ClientConnect',
						'ImportNonuwmClients',
					);
				}
			});

			const exportContacts = {
				icon: ['fas', 'share'],
				text: `Export Contacts (${this.initialTotalCount})`,
				action: 'exportContacts',
				disabled: false,
			};

			const quickActions = [addSingleContact, showContactsImportModal];

			if (this.userRole.includes('BrokerOwner')) {
				quickActions.push(exportContacts);
			}

			if (this.mainProfileIsInternalUser) {
				disableAllQuickActions([quickActions]);
			}

			return [quickActions];
		},
		async handleExportContacts() {
			try {
				await this.exportContacts();
			} catch (error) {
				this.$emit('apiCallError', 'exportContact');
			}
		},
		handleSearchInputChange(searchTerm) {
			this.currentFilter = TYPES.SEARCH;
			this.newSearch = searchTerm;
			this.checkSelectedContacts();
		},
		handleBrokerChange(selectedBrokerIds) {
			this.currentFilter = TYPES.OWNER;
			this.newBrokers = [...selectedBrokerIds];
			this.checkSelectedContacts();
		},
		handleFilterChange({ filters }) {
			this.currentFilter = TYPES.FILTERS;
			this.newContactTypeFilters = filters[TYPES.CONTACT]
				? filters[TYPES.CONTACT]
				: [];
			this.newLoanTypeFilters = filters[TYPES.LOAN_TYPE]
				? filters[TYPES.LOAN_TYPE]
				: [];
			this.newLoanPurposeFilters = filters[TYPES.LOAN_PURPOSE]
				? filters[TYPES.LOAN_PURPOSE]
				: [];
			this.checkSelectedContacts();
		},
		checkSelectedContacts() {
			if (this.selectedLeadsCount > 0 || this.selectAllMode) {
				this.openConfirmFilterSelectionModal();
			} else {
				this.filterChange();
			}
		},
		filterChange() {
			switch (this.currentFilter) {
				case TYPES.SEARCH:
					this.updatefiltersSearchTerm(this.newSearch);
					break;
				case TYPES.OWNER:
					this.updateSelectedContactsIds([...this.newBrokers]);
					break;
				case TYPES.FILTERS:
					this.updatefiltersTypesIds(this.newContactTypeFilters);
					this.selectedFilters[TYPES.CONTACT] = this.typesIds;
					this.updatefiltersLoanTypeIds(this.newLoanTypeFilters);
					this.selectedFilters[TYPES.LOAN_TYPE] = this.loanTypeIds;
					this.updatefiltersLoanPurposeIds(this.newLoanPurposeFilters);
					this.selectedFilters[TYPES.LOAN_PURPOSE] = this.loanPurposeIds;
					break;
			}

			this.updateContacts();
			this.clearComponentFilters();
		},
		clearComponentFilters() {
			this.currentFilter = '';
			this.newSearch = '';
			this.newBrokers = [];
			this.newContactTypeFilters = [];
			this.newLoanTypeFilters = [];
			this.newLoanPurposeFilters = [];
		},
		openConfirmFilterSelectionModal() {
			this.setModalView(MODAL_VIEWS.CONFIRM_FILTER_SELECTION);
			this.setShowOverviewModal(true);
		},
		handleCancelSearch() {
			this.cancelSearch = false;
		},
		handleCancelBrokerChange() {
			this.cancelBrokerChange = false;
		},
		handleCancelFilterChange() {
			this.cancelFilterChange = false;
		},
		updateContacts() {
			this.$emit('updateContacts');
		},
	},
	watch: {
		async onInit(value) {
			/**
			 * When ccv2.0 state is reset when the user is on this page, repopulate the data
			 * (this will happen when profile is switched/impersonation)
			 */
			if (value) {
				this.updateContacts();
				await this.$nextTick();
				this.selectedFilters[TYPES.CONTACT] = this.typesIds;
				this.selectedFilters[TYPES.LOAN_TYPE] = this.loanTypeIds;
				this.selectedFilters[TYPES.LOAN_PURPOSE] = this.loanPurposeIds;
				this.setLeadsOninit(false);
			}
		},
		filterStatus(newValue) {
			if (newValue === 'confirmed') {
				this.setSelectedLeadIds([]);
				this.$emit('cancelSelectAllMode');
				this.filterChange();
			} else if (newValue === 'canceled') {
				this.cancelSearch = true;
				this.cancelBrokerChange = true;
				this.cancelFilterChange = true;
				this.clearComponentFilters();
			}
			this.setShowOverviewModal(false);
			this.$emit('resetFilterStatus');
		},
	},
	async created() {
		this.loading = true;
		await Promise.allSettled([
			this.getAllLeadTypes(),
			this.getLoanTypes(),
			this.getLoanPurposeTypes(),
		]);
		this.loading = false;
	},
	mounted() {
		this.selectedFilters[TYPES.CONTACT] = this.typesIds;
		this.selectedFilters[TYPES.LOAN_TYPE] = this.loanTypeIds;
		this.selectedFilters[TYPES.LOAN_PURPOSE] = this.loanPurposeIds;
	},
};
</script>

<style scoped lang="scss">
.contact-actions-container {
	display: flex;
	gap: 15px;

	.search-container {
		flex-grow: 1;
	}

	@media only screen and (max-width: 600px) {
		display: grid;
		grid-template-rows: 1fr 1fr;
		grid-template-columns: 1fr auto auto;
		grid-template-areas:
			'search search search'
			'contacts owner filter';

		.contact-search {
			grid-area: search;
		}

		.add-contacts {
			margin-left: 0;
			grid-area: contacts;
		}

		.owner-menu {
			grid-area: owner;
		}
		.multiple-filters {
			grid-area: filter;
		}
	}
}
</style>
